import * as _ from 'lodash-es';
import React from 'react';
import Toast from '../../components/Toast';
import { TopicPrivacy } from '../../graphql/generated/graphql';

const PRIVACY_TYPE_MAPPER = {
  PRIVATE: 'restricted',
  SECRET: 'restricted',
  OPEN: 'open',
  PUBLIC: 'open',
};

const splitToOpenAndRestrictedSet = (
  privacies: TopicPrivacy[],
): [Set<TopicPrivacy>, Set<TopicPrivacy>] => {
  const { open = [], restricted = [] } = _.groupBy(
    privacies,
    (privacy) => PRIVACY_TYPE_MAPPER[privacy],
  );

  return [new Set(open), new Set(restricted)];
};

type TopicShape = { id: string; name: string; privacy: TopicPrivacy };
interface getToastPropsParameters {
  adding: boolean;
  newTopic: TopicShape;
  oldTopics: TopicShape[];
  organizationName: string;
  published: boolean;
}

const getToastProps = ({
  adding,
  newTopic,
  oldTopics,
  organizationName,
  published,
}: getToastPropsParameters) => {
  if (!published) return null;

  const newTopics = adding
    ? [...oldTopics, newTopic]
    : oldTopics.filter(({ id }) => id !== newTopic.id);

  const [oldTopicsOpenSet, oldTopicsRestrictedSet] =
    splitToOpenAndRestrictedSet(oldTopics.map((topic) => topic.privacy));

  const [newTopicsOpenSet, newTopicsRestrictedSet] =
    splitToOpenAndRestrictedSet(newTopics.map((topic) => topic.privacy));

  const wasEmpty =
    oldTopicsOpenSet.size === 0 && oldTopicsRestrictedSet.size === 0;
  const isEmpty =
    newTopicsOpenSet.size === 0 && newTopicsRestrictedSet.size === 0;
  const wasOpen = wasEmpty || oldTopicsOpenSet.size > 0;
  const isOpen = isEmpty || newTopicsOpenSet.size > 0;
  const wasRestricted = !wasOpen;
  const isRestricted = !isOpen;

  if (wasEmpty) {
    if (isRestricted) {
      return {
        title: "This post is now only accessible by its topic's members",
        message: `${
          newTopicsRestrictedSet.has(TopicPrivacy.Secret) ? 'Secret' : 'Private'
        } topics are only accessible by their members`,
        undo: true,
      };
    }
    return null;
  }

  if (wasOpen) {
    if (isRestricted) {
      return {
        title: "This post is now only accessible by its topic's members",
        message:
          newTopicsRestrictedSet.size > 1
            ? 'Posts with both private and secret topics are accessible by members of either topic'
            : `${
                newTopicsRestrictedSet.has(TopicPrivacy.Secret)
                  ? 'Secret'
                  : 'Private'
              } topics are only accessible by their members`,
        undo: true,
      };
    }
    if (isOpen) {
      // Is still open but added some restricted topic
      if (newTopicsRestrictedSet.size > 0) {
        return {
          title: `This post will remain accessible by anyone at ${organizationName}`,
          message: `Posts with ${
            newTopicsRestrictedSet.size === 1
              ? `both open and ${
                  newTopicsRestrictedSet.has(TopicPrivacy.Secret)
                    ? 'secret'
                    : 'private'
                }`
              : 'open, secret and private'
          } topics are accessible by anyone`,
          undo: false,
        };
      }
    }
    return null;
  }

  if (wasRestricted) {
    if (isEmpty) {
      return {
        title: `This post is now accessible by anyone at ${organizationName}`,
        message: 'Posts with no topics are accessible by anyone',
        undo: true,
      };
    }
    if (isOpen) {
      return {
        title: `This post is now accessible by anyone at ${organizationName}`,
        message: `Posts with ${
          newTopicsRestrictedSet.size === 1
            ? `both open and ${
                newTopicsRestrictedSet.has(TopicPrivacy.Secret)
                  ? 'secret'
                  : 'private'
              }`
            : 'open, secret and private'
        } topics are accessible by anyone`,
        undo: true,
      };
    }
    if (isRestricted) {
      if (oldTopicsRestrictedSet.size === newTopicsRestrictedSet.size) {
        // Added already existed topic type
        return null;
      }
      return {
        title: "This post will remain accessible by its topic's members",
        message:
          newTopicsRestrictedSet.size > 1
            ? 'Posts with both private and secret topics are accessible by members of either topic'
            : `${
                newTopicsRestrictedSet.has(TopicPrivacy.Secret)
                  ? 'Secret'
                  : 'Private'
              } topics are only accessible by their members`,
        undo: false,
      };
    }
    return null;
  }
  return null;
};

export const shouldShowToast = ({
  oldTopics,
  adding,
  newTopic,
  organizationName,
  published,
}: getToastPropsParameters) =>
  !!getToastProps({
    oldTopics,
    adding,
    newTopic,
    organizationName,
    published,
  });

interface PostTopicToastProps {
  adding: boolean;
  addTopic: (topic: { id: string }) => unknown;
  onDismiss: () => unknown;
  organizationName: string;
  postTopics: TopicShape[];
  published: boolean;
  removeTopic: (topic: { id: string; name: string }) => unknown;
  topic: TopicShape;
}

const PostTopicToast = ({
  adding,
  addTopic,
  onDismiss,
  organizationName,
  postTopics,
  published,
  removeTopic,
  topic,
}: PostTopicToastProps) => {
  const toastProps = getToastProps({
    oldTopics: postTopics,
    adding,
    newTopic: topic,
    organizationName,
    published,
  });

  return (
    toastProps && (
      <Toast
        title={toastProps.title}
        message={toastProps.message}
        secondaryText={toastProps.undo ? 'Undo' : 'Got it'}
        onSecondaryClick={
          toastProps.undo
            ? () => {
                const undo = adding ? removeTopic : addTopic;
                undo(topic);
                onDismiss();
              }
            : onDismiss
        }
        onDismiss={onDismiss}
      />
    )
  );
};

export default PostTopicToast;
